import React, { useState, useEffect } from 'react';
import {
  Store,
  Package,
  CreditCard,
  Users,
  BarChart3,
  Check,
  X,
  Plus,
  Minus,
  Search,
  Sparkles,
  Clock,
  AlertTriangle,
  FileText,
  Receipt,
  UserCog,
  ShoppingCart,
  FolderKanban,
  TicketCheck,
  ScanBarcode,
  Globe,
  Mic,
  Files,
  Zap,
  ChevronRight,
  RefreshCw,
  Calendar,
  DollarSign,
  ArrowUpRight,
  Settings,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  Users, FileText, Receipt, UserCog, Package, ShoppingCart,
  FolderKanban, TicketCheck, ScanBarcode, Globe, Mic, Files, BarChart3
};

const AppStore = () => {
  const { currencySymbol } = useSettings();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('modules');
  const [subscription, setSubscription] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [perUserPricing, setPerUserPricing] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Module detail dialog
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  
  // Billing
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState('usd');

  const organizationId = user?.organization_id || user?.id || 'default-org';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [modulesRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/modules`),
        axios.get(`${API_URL}/api/subscriptions/current/${organizationId}`)
      ]);
      
      setAllModules(modulesRes.data.modules);
      setPerUserPricing(modulesRes.data.per_user_pricing);
      
      if (subRes.data.subscription) {
        setSubscription(subRes.data.subscription);
        setBillingCycle(subRes.data.subscription.billing_cycle || 'monthly');
        setCurrency(subRes.data.subscription.currency || 'usd');
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      await axios.post(`${API_URL}/api/subscriptions/start-trial`, {
        organization_id: organizationId,
        organization_name: user?.organization_name || 'My Organization',
        user_count: 3,
        billing_cycle: billingCycle,
        currency: currency
      });
      toast.success('🎉 Trial started! All modules unlocked for 14 days.');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start trial');
    }
  };

  const handleToggleModule = async (moduleId, currentlyEnabled) => {
    if (!subscription) {
      toast.error('Please start a trial or subscribe first');
      return;
    }
    
    if (subscription.status === 'trial') {
      toast.info('All modules are available during your trial period');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/subscriptions/${subscription.id}/modules`, {
        module_id: moduleId,
        action: currentlyEnabled ? 'remove' : 'add'
      });
      toast.success(currentlyEnabled ? 'Module disabled' : 'Module enabled');
      loadData();
      setShowModuleDialog(false);
    } catch (err) {
      toast.error('Failed to update module');
    }
  };

  const handleUpdateUsers = async (newCount) => {
    if (!subscription) return;
    
    try {
      await axios.put(`${API_URL}/api/subscriptions/${subscription.id}/users`, null, {
        params: { user_count: Math.max(1, newCount) }
      });
      toast.success('Team size updated');
      loadData();
    } catch (err) {
      toast.error('Failed to update team size');
    }
  };

  const isModuleEnabled = (moduleId) => {
    return subscription?.modules?.includes(moduleId) || false;
  };

  const getModulePrice = (module) => {
    const priceKey = `price_${currency}_${billingCycle}`;
    return module[priceKey] || 0;
  };

  const billingCurrencySymbol = currency === 'usd' ? '$' : 'SAR ';
  const periodLabel = billingCycle === 'monthly' ? '/mo' : '/yr';

  // Calculate trial days remaining
  let trialDaysRemaining = 0;
  if (subscription?.status === 'trial' && subscription?.trial_end) {
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    trialDaysRemaining = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
  }

  // Filter modules
  const categories = ['all', ...new Set(allModules.map(m => m.is_addon ? 'Add-ons' : 'Core'))];
  const filteredModules = allModules.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          m.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           (selectedCategory === 'Add-ons' && m.is_addon) ||
                           (selectedCategory === 'Core' && !m.is_addon);
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="h-7 w-7 text-primary" />
            App Store
          </h1>
          <p className="text-muted-foreground">
            Discover, enable, and manage your business modules
          </p>
        </div>
        
        {subscription && (
          <div className="flex items-center gap-2">
            <Badge variant={subscription.status === 'trial' ? 'default' : 'secondary'} className="text-sm px-3 py-1">
              {subscription.status === 'trial' ? (
                <><Sparkles className="h-3 w-3 mr-1" /> Trial ({trialDaysRemaining} days left)</>
              ) : subscription.status === 'active' ? (
                <><Check className="h-3 w-3 mr-1" /> Active</>
              ) : (
                subscription.status
              )}
            </Badge>
          </div>
        )}
      </div>

      {/* No Subscription - Trial CTA */}
      {!subscription && (
        <Card className="bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-500 text-white border-0 overflow-hidden">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Start Your Free Trial</h2>
              <p className="text-white/80 mb-4">
                Get access to all {allModules.length} modules for 14 days. No credit card required.
              </p>
              <div className="flex items-center gap-4 text-sm text-white/70">
                <span className="flex items-center gap-1"><Check className="h-4 w-4" /> All modules</span>
                <span className="flex items-center gap-1"><Check className="h-4 w-4" /> 3 free users</span>
                <span className="flex items-center gap-1"><Check className="h-4 w-4" /> Full features</span>
              </div>
            </div>
            <Button 
              size="lg" 
              className="bg-white text-purple-600 hover:bg-white/90 shadow-lg"
              onClick={handleStartTrial}
            >
              <Zap className="h-5 w-5 mr-2" />
              Start 14-Day Trial
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trial Banner */}
      {subscription?.status === 'trial' && (
        <Card className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-0">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5" />
              <div>
                <p className="font-semibold">Trial Period Active</p>
                <p className="text-sm text-white/80">
                  {trialDaysRemaining} days remaining • All {allModules.length} modules unlocked
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={(14 - trialDaysRemaining) / 14 * 100} className="w-32 h-2 bg-white/30" />
              <Button variant="secondary" className="bg-white text-purple-600 hover:bg-white/90">
                Upgrade Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="modules" className="gap-2">
            <Package className="h-4 w-4" /> Modules
          </TabsTrigger>
          <TabsTrigger value="billing" className="gap-2">
            <CreditCard className="h-4 w-4" /> Billing
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" /> Team
          </TabsTrigger>
        </TabsList>

        {/* MODULES TAB */}
        <TabsContent value="modules" className="space-y-4 mt-6">
          {/* Search & Filter */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'Core', 'Add-ons'].map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat === 'all' ? 'All' : cat}
                </Button>
              ))}
            </div>
          </div>

          {/* Modules Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredModules.map(module => {
              const Icon = iconMap[module.icon] || Package;
              const enabled = isModuleEnabled(module.id);
              const price = getModulePrice(module);
              
              return (
                <Card 
                  key={module.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    enabled ? 'ring-2 ring-primary bg-primary/5' : ''
                  }`}
                  onClick={() => {
                    setSelectedModule(module);
                    setShowModuleDialog(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                        enabled 
                          ? 'bg-primary text-white' 
                          : 'bg-slate-100 dark:bg-slate-800 text-muted-foreground'
                      }`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex items-center gap-2">
                        {module.is_popular && (
                          <Badge variant="secondary" className="text-[10px]">Popular</Badge>
                        )}
                        {module.is_addon && (
                          <Badge className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900/30">Add-on</Badge>
                        )}
                        {enabled && (
                          <div className="h-6 w-6 rounded-full bg-emerald-500 flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="font-semibold mb-1">{module.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {module.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-primary">
                        {currencySymbol}{price}
                        <span className="text-sm font-normal text-muted-foreground">{periodLabel}</span>
                      </span>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* BILLING TAB */}
        <TabsContent value="billing" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Current Plan */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Current Plan</CardTitle>
                <CardDescription>
                  {subscription?.modules?.length || 0} modules active
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Billing Cycle Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <p className="font-medium">Billing Cycle</p>
                    <p className="text-sm text-muted-foreground">Save 17% with yearly billing</p>
                  </div>
                  <div className="flex items-center gap-2 bg-white dark:bg-slate-700 rounded-full p-1">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        billingCycle === 'monthly' 
                          ? 'bg-primary text-white' 
                          : 'text-muted-foreground'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingCycle('yearly')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        billingCycle === 'yearly' 
                          ? 'bg-primary text-white' 
                          : 'text-muted-foreground'
                      }`}
                    >
                      Yearly
                    </button>
                  </div>
                </div>

                {/* Currency Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <p className="font-medium">Currency</p>
                    <p className="text-sm text-muted-foreground">Choose your preferred currency</p>
                  </div>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="usd">USD ($)</SelectItem>
                      <SelectItem value="sar">SAR (﷼)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Active Modules Summary */}
                <div className="pt-4">
                  <p className="font-medium mb-3">Active Modules</p>
                  <div className="flex flex-wrap gap-2">
                    {subscription?.modules?.map(moduleId => {
                      const module = allModules.find(m => m.id === moduleId);
                      if (!module) return null;
                      return (
                        <Badge key={moduleId} variant="secondary" className="gap-1">
                          <Check className="h-3 w-3" />
                          {module.name}
                        </Badge>
                      );
                    })}
                    {(!subscription?.modules || subscription.modules.length === 0) && (
                      <p className="text-sm text-muted-foreground">No modules active</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Billing Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Modules ({subscription?.modules?.length || 0})</span>
                    <span>{currencySymbol}{subscription?.module_total || 0}</span>
                  </div>
                  {(subscription?.user_total || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Extra Users</span>
                      <span>{currencySymbol}{subscription?.user_total}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-primary">
                      {currencySymbol}{subscription?.total || 0}
                      <span className="text-sm font-normal text-muted-foreground">{periodLabel}</span>
                    </span>
                  </div>
                </div>

                {subscription?.status === 'active' && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Next billing: {subscription?.current_period_end 
                        ? new Date(subscription.current_period_end).toLocaleDateString()
                        : 'N/A'
                      }</span>
                    </div>
                  </div>
                )}

                {subscription?.status !== 'trial' && (
                  <Button className="w-full" variant="outline">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Add Payment Method
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TEAM TAB */}
        <TabsContent value="team" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Team Size */}
            <Card>
              <CardHeader>
                <CardTitle>Team Size</CardTitle>
                <CardDescription>
                  First {perUserPricing?.free_users || 3} users are free with any subscription
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-6 py-6">
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="h-12 w-12"
                    onClick={() => handleUpdateUsers((subscription?.user_count || 1) - 1)}
                    disabled={!subscription || subscription.user_count <= 1}
                  >
                    <Minus className="h-5 w-5" />
                  </Button>
                  <div className="text-center">
                    <p className="text-5xl font-bold">{subscription?.user_count || 1}</p>
                    <p className="text-muted-foreground">users</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="h-12 w-12"
                    onClick={() => handleUpdateUsers((subscription?.user_count || 1) + 1)}
                    disabled={!subscription}
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>

                {/* Pricing breakdown */}
                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Free users included</span>
                    <span>{perUserPricing?.free_users || 3}</span>
                  </div>
                  {(subscription?.user_count || 0) > (perUserPricing?.free_users || 3) && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Extra users</span>
                        <span>{(subscription?.user_count || 0) - (perUserPricing?.free_users || 3)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Price per extra user</span>
                        <span>{currencySymbol}{perUserPricing?.[`${currency}_${billingCycle}`] || 10}{periodLabel}</span>
                      </div>
                      <div className="flex justify-between font-medium pt-2 border-t">
                        <span>Extra users total</span>
                        <span>{currencySymbol}{subscription?.user_total || 0}{periodLabel}</span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* User Roles */}
            <Card>
              <CardHeader>
                <CardTitle>User Seats</CardTitle>
                <CardDescription>Manage who has access to your workspace</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {user?.name?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="font-medium">{user?.name || 'Admin User'}</p>
                        <p className="text-sm text-muted-foreground">{user?.email || 'admin@example.com'}</p>
                      </div>
                    </div>
                    <Badge>Owner</Badge>
                  </div>

                  <Button variant="outline" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Invite Team Member
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    You can add up to {subscription?.user_count || 1} users. {' '}
                    {(subscription?.user_count || 1) <= (perUserPricing?.free_users || 3) 
                      ? `${(perUserPricing?.free_users || 3) - (subscription?.user_count || 1)} free seats remaining.`
                      : 'Additional seats are paid.'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Module Detail Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
        <DialogContent className="max-w-lg">
          {selectedModule && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = iconMap[selectedModule.icon] || Package;
                    return (
                      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                        isModuleEnabled(selectedModule.id) 
                          ? 'bg-primary text-white' 
                          : 'bg-slate-100 dark:bg-slate-800'
                      }`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    );
                  })()}
                  <div>
                    <DialogTitle className="text-xl">{selectedModule.name}</DialogTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedModule.is_popular && (
                        <Badge variant="secondary" className="text-xs">Popular</Badge>
                      )}
                      {selectedModule.is_addon && (
                        <Badge className="text-xs bg-purple-100 text-purple-700">Add-on</Badge>
                      )}
                      {isModuleEnabled(selectedModule.id) && (
                        <Badge className="text-xs bg-emerald-100 text-emerald-700">Active</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-muted-foreground">{selectedModule.description}</p>
                
                <div>
                  <p className="font-medium mb-2">Features included:</p>
                  <ul className="space-y-2">
                    {selectedModule.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <span className="font-medium">Price</span>
                  <span className="text-2xl font-bold text-primary">
                    {currencySymbol}{getModulePrice(selectedModule)}
                    <span className="text-sm font-normal text-muted-foreground">{periodLabel}</span>
                  </span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowModuleDialog(false)}>
                  Cancel
                </Button>
                {subscription?.status === 'trial' ? (
                  <Button disabled className="gap-2">
                    <Check className="h-4 w-4" /> Included in Trial
                  </Button>
                ) : isModuleEnabled(selectedModule.id) ? (
                  <Button 
                    variant="destructive"
                    onClick={() => handleToggleModule(selectedModule.id, true)}
                  >
                    <X className="h-4 w-4 mr-2" /> Disable Module
                  </Button>
                ) : (
                  <Button onClick={() => handleToggleModule(selectedModule.id, false)}>
                    <Plus className="h-4 w-4 mr-2" /> Enable Module
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppStore;
